/**
 * BacktestTab — port of callbacks/backtest.
 *
 * Form: symbol / period / fast SMA / slow SMA → POST /api/backtest.
 * Renders the equity curve + summary stats (total return, annualized,
 * Sharpe, max drawdown, trades, win rate). The router worker runs the
 * SMA-crossover strategy in-Worker and returns the result synchronously
 * (with a job_id) — we poll /api/backtest/{job_id} until status='complete'.
 */
import { useState, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LazyPlot as Plot } from '@/components/LazyPlot';
import { useStore, readThemeColors } from '@/store';
import { api } from '@/lib/api';
import {
  sharpe, sortino, calmar, omega,
  historicalVaR, cvar, drawdown,
  skewness, kurtosis, tailRatio,
  mean, stdev,
} from '@/lib/quantMetrics';
import { ORG, RED, MUT, FONT } from '@/theme';

interface BacktestResult {
  id:               string;
  status:           'complete' | 'running' | 'error';
  strategy:         string;
  symbol:           string;
  startDate:        string;
  endDate:          string;
  totalReturn:      number;
  annualizedReturn: number;
  sharpe:           number;
  maxDrawdown:      number;
  trades:           number;
  winRate:          number;
  equityCurve:      Array<{ date: string; value: number }>;
}

const PERIODS = ['6mo', '1y', '2y', '5y'] as const;

export function BacktestTab() {
  const focus = useStore(s => s.focusSymbol);
  const [symbol, setSymbol] = useState(focus);
  const [period, setPeriod] = useState<typeof PERIODS[number]>('1y');
  const [fast, setFast] = useState(20);
  const [slow, setSlow] = useState(50);
  const [jobId, setJobId] = useState<string | null>(null);

  // POST /api/backtest → returns { job_id }
  const start = useMutation({
    mutationFn: (params: { symbol: string; period: string; fast: number; slow: number }) =>
      api<{ job_id: string }>('/api/backtest', { method: 'POST', body: JSON.stringify(params) }),
    onSuccess: (r) => setJobId(r.job_id),
  });

  // Poll /api/backtest/{job_id} until complete.
  const { data: result } = useQuery<BacktestResult | null>({
    queryKey: ['backtest-job', jobId],
    queryFn:  async () => jobId ? api<BacktestResult>(`/api/backtest/${jobId}`) : null,
    enabled:  !!jobId,
    refetchInterval: (q) => (q.state.data?.status === 'complete' || q.state.data?.status === 'error') ? false : 1500,
  });

  const onRun = () => {
    if (slow <= fast) { return; }
    start.mutate({ symbol, period, fast, slow });
  };

  const equityX = result?.equityCurve.map(p => p.date) ?? [];
  const equityY = result?.equityCurve.map(p => p.value) ?? [];

  // Derive the full ratio set from the equity curve. Server returns
  // Sharpe + maxDrawdown but not the rest, so we compute them client-side
  // off the daily returns implied by consecutive equity values.
  const stats = useMemo(() => {
    if (!result || !result.equityCurve || result.equityCurve.length < 3) return null;
    const eq = result.equityCurve.map(p => p.value);
    const rets: number[] = [];
    for (let i = 1; i < eq.length; i++) rets.push(eq[i] / eq[i - 1] - 1);
    const dd = drawdown(eq);
    return {
      // ONE SHARPE, FROM THE SERVER.
      //
      // This re-derived Sharpe client-side at rf = 4.5% while the server
      // computed it at rf = 0, and the UI then showed THIS number beside
      // the SERVER's max drawdown -- two different Sharpes for one
      // backtest, on one screen, with no way for a user to tell which
      // they were reading. The server now computes excess-return Sharpe
      // once and returns it; trust that.
      sharpe:   typeof result?.sharpe === "number" ? result.sharpe : sharpe(rets),
      sortino:  sortino(rets),
      calmar:   calmar(rets),
      omega:    omega(rets),
      var95:    historicalVaR(rets, 0.95),
      cvar95:   cvar(rets, 0.95),
      ddSeries: dd.series,
      ddDur:    dd.maxDurationBars,
      skew:     skewness(rets),
      kurt:     kurtosis(rets),
      tail:     tailRatio(rets),
      vol:      stdev(rets) * Math.sqrt(252),
      meanRet:  mean(rets) * 252,
      rets,
    };
  }, [result]);

  const { gain: GAIN, loss: LOSS, accent: ACC } = readThemeColors();

  return (
    <div style={{ fontFamily: FONT, color: '#e0e0e0' }}>
      {/* Form */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginBottom: 8, flexWrap: 'wrap' }}>
        <Field label="SYMBOL">
          <input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                 style={{ width: 80, fontSize: 11, height: 22, padding: '2px 6px' }} />
        </Field>
        <Field label="PERIOD">
          <select value={period} onChange={(e) => setPeriod(e.target.value as typeof PERIODS[number])}
                  style={{ width: 70, fontSize: 11, height: 22, padding: '1px 4px' }}>
            {PERIODS.map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
          </select>
        </Field>
        <Field label="FAST SMA">
          <input type="number" value={fast} onChange={(e) => setFast(Number(e.target.value || 0))}
                 style={{ width: 70, fontSize: 11, height: 22, padding: '2px 6px' }} />
        </Field>
        <Field label="SLOW SMA">
          <input type="number" value={slow} onChange={(e) => setSlow(Number(e.target.value || 0))}
                 style={{ width: 70, fontSize: 11, height: 22, padding: '2px 6px' }} />
        </Field>
        <button onClick={onRun} disabled={start.isPending} style={{
          background: ORG, color: '#000', border: 'none',
          fontFamily: FONT, fontSize: 10, fontWeight: 'bold',
          padding: '4px 16px', borderRadius: 2, height: 22,
        }}>
          {start.isPending ? 'RUNNING...' : 'RUN BACKTEST'}
        </button>
        {result && result.status !== 'complete' && (
          <span style={{ color: MUT, fontSize: 10 }}>Polling job {result.id}…</span>
        )}
      </div>

      {/* Stats strip + charts */}
      {result && result.status === 'complete' && stats && (
        <>
          {/* Row 1: returns + Sharpe family */}
          <div style={{
            display: 'flex', gap: 18, marginBottom: 4,
            padding: '4px 0', borderBottom: '1px solid #222', fontSize: 11, flexWrap: 'wrap',
          }}>
            <Stat label="STRATEGY"  value={result.strategy} />
            <Stat label="WINDOW"    value={`${result.startDate} → ${result.endDate}`} />
            <Stat label="TOTAL RET" value={`${(result.totalReturn * 100).toFixed(2)}%`}
                  color={result.totalReturn >= 0 ? GAIN : LOSS} />
            <Stat label="ANN RET"   value={`${(result.annualizedReturn * 100).toFixed(2)}%`}
                  color={result.annualizedReturn >= 0 ? GAIN : LOSS} />
            <Stat label="ANN VOL"   value={`${(stats.vol * 100).toFixed(2)}%`} />
          </div>
          {/* Row 2: ratio family */}
          <div style={{
            display: 'flex', gap: 18, marginBottom: 4,
            padding: '4px 0', borderBottom: '1px solid #222', fontSize: 11, flexWrap: 'wrap',
          }}>
            <Stat label="SHARPE"  value={stats.sharpe.toFixed(2)}  color={stats.sharpe  >= 1 ? GAIN : stats.sharpe  >= 0 ? ACC : LOSS} />
            <Stat label="SORTINO" value={stats.sortino.toFixed(2)} color={stats.sortino >= 1 ? GAIN : stats.sortino >= 0 ? ACC : LOSS} />
            <Stat label="CALMAR"  value={stats.calmar.toFixed(2)}  color={stats.calmar  >= 1 ? GAIN : stats.calmar  >= 0 ? ACC : LOSS} />
            <Stat label="OMEGA"   value={Number.isFinite(stats.omega) ? stats.omega.toFixed(2) : '∞'} />
            <Stat label="TAIL"    value={Number.isFinite(stats.tail) ? stats.tail.toFixed(2) : '—'} />
            <Stat label="TRADES"  value={String(result.trades)} />
            <Stat label="WIN %"   value={`${(result.winRate * 100).toFixed(1)}%`} />
          </div>
          {/* Row 3: risk family */}
          <div style={{
            display: 'flex', gap: 18, marginBottom: 6,
            padding: '4px 0', borderBottom: '1px solid #222', fontSize: 11, flexWrap: 'wrap',
          }}>
            <Stat label="MAX DD"     value={`-${(result.maxDrawdown * 100).toFixed(2)}%`} color={LOSS} />
            <Stat label="DD DUR"     value={`${stats.ddDur}d`} />
            <Stat label="VaR 95"     value={`-${(stats.var95  * 100).toFixed(2)}%`} color={LOSS} />
            <Stat label="CVaR 95"    value={`-${(stats.cvar95 * 100).toFixed(2)}%`} color={LOSS} />
            <Stat label="SKEW"       value={stats.skew.toFixed(2)}  color={stats.skew >= 0 ? GAIN : LOSS} />
            <Stat label="KURT"       value={stats.kurt.toFixed(2)} />
          </div>

          {/* Equity curve */}
          <div style={{ height: 220, marginBottom: 8 }}>
            <Plot
              data={[{
                type: 'scatter', mode: 'lines', name: 'Equity',
                x: equityX, y: equityY,
                line: { color: ACC, width: 1.5 },
                fill: 'tozeroy', fillcolor: 'rgba(127,127,127,0.05)',
              } as unknown as Plotly.Data]}
              layout={{
                paper_bgcolor: '#090909', plot_bgcolor: '#090909',
                margin: { l: 60, r: 30, t: 4, b: 24 },
                font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 9 },
                xaxis: { gridcolor: '#1a1a1a', color: MUT },
                yaxis: { gridcolor: '#1a1a1a', color: MUT, tickprefix: '$' },
                showlegend: false,
              } as unknown as Partial<Plotly.Layout>}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler
            />
          </div>

          {/* Drawdown chart + returns distribution side by side */}
          <div style={{ display: 'flex', gap: 12, height: 180 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: ORG, fontSize: 10, letterSpacing: 1, marginBottom: 2 }}>
                DRAWDOWN
              </div>
              <Plot
                data={[{
                  type: 'scatter', mode: 'lines', name: 'DD',
                  x: equityX,
                  y: stats.ddSeries.map(d => d * 100),
                  line: { color: LOSS, width: 1.2 },
                  fill: 'tozeroy', fillcolor: 'rgba(255,51,51,0.15)',
                } as unknown as Plotly.Data]}
                layout={{
                  paper_bgcolor: '#090909', plot_bgcolor: '#090909',
                  margin: { l: 50, r: 10, t: 4, b: 24 },
                  font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 9 },
                  xaxis: { gridcolor: '#1a1a1a', color: MUT },
                  yaxis: { gridcolor: '#1a1a1a', color: MUT, ticksuffix: '%' },
                  showlegend: false,
                } as unknown as Partial<Plotly.Layout>}
                config={{ displayModeBar: false, responsive: true }}
                style={{ width: '100%', height: '100%' }}
                useResizeHandler
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: ORG, fontSize: 10, letterSpacing: 1, marginBottom: 2 }}>
                DAILY RETURNS DISTRIBUTION
              </div>
              <Plot
                data={[{
                  type: 'histogram', name: 'rets',
                  x: stats.rets.map(r => r * 100),
                  nbinsx: 40,
                  marker: { color: ACC, opacity: 0.7 },
                } as unknown as Plotly.Data]}
                layout={{
                  paper_bgcolor: '#090909', plot_bgcolor: '#090909',
                  margin: { l: 40, r: 10, t: 4, b: 24 },
                  font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 9 },
                  xaxis: { gridcolor: '#1a1a1a', color: MUT, ticksuffix: '%' },
                  yaxis: { gridcolor: '#1a1a1a', color: MUT },
                  showlegend: false,
                  // VaR + CVaR lines
                  shapes: [
                    { type: 'line', xref: 'x', x0: -stats.var95 * 100, x1: -stats.var95 * 100, yref: 'paper', y0: 0, y1: 1,
                      line: { color: LOSS, width: 1, dash: 'dash' } },
                    { type: 'line', xref: 'x', x0: -stats.cvar95 * 100, x1: -stats.cvar95 * 100, yref: 'paper', y0: 0, y1: 1,
                      line: { color: '#cc66ff', width: 1, dash: 'longdash' } },
                  ],
                } as unknown as Partial<Plotly.Layout>}
                config={{ displayModeBar: false, responsive: true }}
                style={{ width: '100%', height: '100%' }}
                useResizeHandler
              />
            </div>
          </div>
        </>
      )}

      {start.isError && (
        <div style={{ color: RED, fontSize: 11 }}>Backtest failed: {(start.error as Error).message}</div>
      )}
      {!result && !start.isPending && (
        <div style={{ color: MUT, fontSize: 11, padding: '20px 0' }}>
          SMA crossover backtest · pulls bars via the router · runs in-Worker · returns equity + stats.
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ color: MUT, fontSize: 9, letterSpacing: 1, marginBottom: 2 }}>{label}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div style={{ color: MUT, fontSize: 9, letterSpacing: 1 }}>{label}</div>
      <div style={{ color: color ?? '#e0e0e0', fontWeight: 'bold', fontSize: 11 }}>{value}</div>
    </div>
  );
}
